import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  Stack,
  Alert,
  Divider,
  useTheme,
  useMediaQuery,
  Autocomplete,
} from "@mui/material";
import {
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Save as SaveIcon,
  NavigateNext as NextIcon,
  Email as EmailIcon,
  AccountBalance as LenderIcon,
} from "@mui/icons-material";
import MainContact from "./MainContact.js";
import { apiCall } from "../../../services/ApiCall";
import config from "../../../config/config.json";
import PlacesAutocomplete, { geocodeByAddress } from "react-places-autocomplete";

const EditCompany = ({ editData, setShowDetails }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  // State variables (keeping original logic)
  const [companyUserData, setCompanyUserData] = useState([]);
  const [data, setData] = useState();
  const [isDisabled, setIsDisabled] = useState();
  const [companyData, setCompanyData] = useState({});
  const [currentPage, setCurrentPage] = useState("editCompany");
  const [errors, setErrors] = useState({});
  const [lenders, setLenders] = useState([
    { id: Date.now(), name: "", email: "" },
  ]);

  const primaryColor = "#0d6efd";

  // Original API functions (unchanged logic)
  const getCompanyContact = async () => {
    const match = location.pathname.match(/\/company-list\/edit\/(\d+)/);
    const { isSuccess, data } = await apiCall(
      `${config.POST_LOGIN_API_URL}getCompanyContact`,
      {},
      { companyId: editData.id },
      "GET"
    );
    if (isSuccess) {
      setCompanyUserData(data?.data?.comapnyUserData);
      setCompanyData(data?.data?.companyData);
    } else {
      console.log("error");
    }
  };

  // Original input change handler (unchanged logic)
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCompanyData({ ...companyData, [name]: value });

    if (name === "araId") {
      if (data.includes(e.target.value)) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          araId: 'This ARA ID already exists.',
        }));
        setIsDisabled(true);
      } else {
        setErrors((prevErrors) => ({
          ...prevErrors,
          araId: null,
        }));
        setIsDisabled(false);
      }
    }
  };

  // Original validation function (unchanged logic)
  const validate = () => {
    let formErrors = {};

    if (!companyData.araId) {
      formErrors.araId = "ARA ID is required";
    }
    if (!companyData.street) {
      formErrors.street = "street is required";
    }
    if (!companyData.city) {
      formErrors.city = "city is required";
    }
    if (!companyData.state) {
      formErrors.state = "state is required";
    }
    if (!companyData.name) {
      formErrors.name = "Company Name is required";
    }
    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  // Original API calls (unchanged logic)
  const getCompanyList = async () => {
    const { isSuccess, data } = await apiCall(
      `${config.POST_LOGIN_API_URL}getCompanyList`,
      {},
      {},
      "GET"
    );
    if (isSuccess) {
      const uniqueAraIds = [...new Set(data?.data?.map(item => item.araId))];
      setData([...new Set(data?.data?.map(item => item.araId))]);
    }
  };

  // Original handlers (unchanged logic)
  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      console.log("Updated company data:", companyData);
    }
  };

  const handleNext = () => {
    if (validate()) {
      setCurrentPage("mainContact");
    }
  };

  const handlePrevious = () => {
    setCurrentPage("editCompany");
  };

  const handleInputChanges = (id, e) => {
    const { name, value } = e.target;
    setLenders(
      lenders.map((lender) =>
        lender.id === id ? { ...lender, [name]: value } : lender
      )
    );
  };

  const addLender = () => {
    setLenders([...lenders, { id: Date.now(), name: "", email: "" }]);
  };

  const removeLender = (id) => {
    setLenders(lenders.filter((lender) => lender.id !== id));
  };

  // Original address select handler (unchanged logic)
  const handleSelect = async (address) => {
    try {
      const results = await geocodeByAddress(address);
      const addressComponents = results[0].address_components;
      const getAddressComponent = (types) => {
        const component = addressComponents.find((comp) =>
          types.some((type) => comp.types.includes(type))
        );
        return component ? component.long_name : "";
      };

      const street =
        getAddressComponent(["street_number"]) +
        " " +
        getAddressComponent(["route"]);
      const city = getAddressComponent(["locality"]);
      const state = getAddressComponent(["administrative_area_level_1"]);
      const zipcode = getAddressComponent(["postal_code"]);

      setCompanyData((prevAnswer) => ({
        ...prevAnswer,
        street: street || address,
        city,
        state,
        zipcode,
      }));
    } catch (error) {
      console.error("Error fetching address details: ", error);
    }
  };

  // Original save handler (unchanged logic)
  const handleSave = async () => {
    const { isSuccess, data } = await apiCall(
      `${config.POST_LOGIN_API_URL}updateCompanyData`,
      {},
      { companyId: companyData.id, ...companyData, officeType: 'Head Office', userData: [] },
      "POST"
    );
    if (isSuccess) {
      setShowDetails(false);
    }
  };

  // Original useEffect hooks (unchanged logic)
  useEffect(() => {
    getCompanyList();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
    } else {
      getCompanyContact();
    }
  }, []);

  // Original conditional rendering (unchanged logic)
  if (currentPage === "mainContact") {
    return (
      <MainContact
        onPrevious={handlePrevious}
        companyData={companyData}
        companyUserData={companyUserData}
        setShowDetails={setShowDetails}
      />
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '100%' }}>
      <form onSubmit={handleSubmit}>
        {/* Company Details Section */}
        <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
          {/* Header */}
          <Box sx={{
            bgcolor: primaryColor,
            color: 'white',
            p: 2.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <BusinessIcon sx={{ fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Company Details
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{
            bgcolor: '#f8f9fa',
            p: { xs: 2, md: 3 }
          }}>
            {/* First Row - ARA ID, Company Name, Street, Street 2 */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="ARA ID"
                  name="araId"
                  value={companyData?.araId || ''}
                  onChange={handleInputChange}
                  error={!!errors.araId}
                  helperText={errors.araId}
                  size={isSmall ? "small" : "medium"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: primaryColor + '40',
                      },
                      '&:hover fieldset': {
                        borderColor: primaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: primaryColor,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Company Name"
                  name="name"
                  value={companyData?.name || ''}
                  onChange={handleInputChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  size={isSmall ? "small" : "medium"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: primaryColor + '40',
                      },
                      '&:hover fieldset': {
                        borderColor: primaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: primaryColor,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mb: 1, 
                      color: primaryColor,
                      fontWeight: 500,
                      fontSize: isSmall ? '0.75rem' : '0.875rem'
                    }}
                  >
                    Street
                  </Typography>
                  <PlacesAutocomplete
                    value={companyData.street || ''}
                    onChange={(value) =>
                      setCompanyData({ ...companyData, street: value })
                    }
                    onSelect={handleSelect}
                  >
                    {({
                      getInputProps,
                      suggestions,
                      getSuggestionItemProps,
                      loading,
                    }) => (
                      <Box sx={{ position: 'relative' }}>
                        <TextField
                          {...getInputProps({ placeholder: "Search Street" })}
                          fullWidth
                          size={isSmall ? "small" : "medium"}
                          error={!!errors.street}
                          helperText={errors.street}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: primaryColor + '40',
                              },
                              '&:hover fieldset': {
                                borderColor: primaryColor,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: primaryColor,
                              },
                            },
                          }}
                        />
                        {(loading || suggestions.length > 0) && (
                          <Paper
                            elevation={3}
                            sx={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              zIndex: 1000,
                              maxHeight: 200,
                              overflow: 'auto',
                            }}
                          >
                            {loading && (
                              <Box sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="body2">Loading...</Typography>
                              </Box>
                            )}
                            {suggestions.map((suggestion, index) => (
                              <Box
                                key={index}
                                {...getSuggestionItemProps(suggestion)}
                                sx={{
                                  p: 2,
                                  cursor: 'pointer',
                                  backgroundColor: suggestion.active ? primaryColor + '10' : 'white',
                                  '&:hover': {
                                    backgroundColor: primaryColor + '20',
                                  },
                                  borderBottom: '1px solid #eee',
                                }}
                              >
                                <Typography variant="body2">
                                  {suggestion.description}
                                </Typography>
                              </Box>
                            ))}
                          </Paper>
                        )}
                      </Box>
                    )}
                  </PlacesAutocomplete>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Street 2"
                  name="street2"
                  value={companyData?.street2 || ''}
                  onChange={handleInputChange}
                  size={isSmall ? "small" : "medium"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: primaryColor + '40',
                      },
                      '&:hover fieldset': {
                        borderColor: primaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: primaryColor,
                    },
                  }}
                />
              </Grid>
            </Grid>

            {/* Second Row - City, State, Zipcode */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={companyData?.city || ''}
                  onChange={handleInputChange}
                  error={!!errors.city}
                  helperText={errors.city}
                  size={isSmall ? "small" : "medium"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: primaryColor + '40',
                      },
                      '&:hover fieldset': {
                        borderColor: primaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: primaryColor,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="State"
                  name="state"
                  value={companyData?.state || ''}
                  onChange={handleInputChange}
                  error={!!errors.state}
                  helperText={errors.state}
                  size={isSmall ? "small" : "medium"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: primaryColor + '40',
                      },
                      '&:hover fieldset': {
                        borderColor: primaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: primaryColor,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Zipcode"
                  name="zipcode"
                  value={companyData?.zipcode || ''}
                  onChange={handleInputChange}
                  size={isSmall ? "small" : "medium"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: primaryColor + '40',
                      },
                      '&:hover fieldset': {
                        borderColor: primaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: primaryColor,
                    },
                  }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Lenders Section */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LenderIcon sx={{ color: primaryColor }} />
                <Typography variant="h6" sx={{ color: primaryColor, fontWeight: 600 }}>
                  Lenders
                </Typography>
              </Box>

              <Stack spacing={2}>
                {lenders.map((lender, index) => (
                  <Card
                    key={lender.id}
                    elevation={0}
                    sx={{
                      border: `1px solid ${primaryColor}20`,
                      borderRadius: 2,
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={5}>
                          <TextField
                            fullWidth
                            label="Lender Name"
                            name="name"
                            value={lender.name}
                            onChange={(e) => handleInputChanges(lender.id, e)}
                            size={isSmall ? "small" : "medium"}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                  borderColor: primaryColor + '40',
                                },
                                '&:hover fieldset': {
                                  borderColor: primaryColor,
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: primaryColor,
                                },
                              },
                              '& .MuiInputLabel-root.Mui-focused': {
                                color: primaryColor,
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={5}>
                          <TextField
                            fullWidth
                            label="Lender Email"
                            name="email"
                            type="email"
                            value={lender.email}
                            onChange={(e) => handleInputChanges(lender.id, e)}
                            size={isSmall ? "small" : "medium"}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                  borderColor: primaryColor + '40',
                                },
                                '&:hover fieldset': {
                                  borderColor: primaryColor,
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: primaryColor,
                                },
                              },
                              '& .MuiInputLabel-root.Mui-focused': {
                                color: primaryColor,
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={2}>
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <IconButton
                              onClick={addLender}
                              sx={{
                                color: primaryColor,
                                bgcolor: `${primaryColor}10`,
                                '&:hover': {
                                  bgcolor: `${primaryColor}20`,
                                }
                              }}
                              size="small"
                            >
                              <AddIcon />
                            </IconButton>
                            {lenders.length > 1 && (
                              <IconButton
                                onClick={() => removeLender(lender.id)}
                                sx={{
                                  color: theme.palette.error.main,
                                  bgcolor: `${theme.palette.error.main}10`,
                                  '&:hover': {
                                    bgcolor: `${theme.palette.error.main}20`,
                                  }
                                }}
                                size="small"
                              >
                                <RemoveIcon />
                              </IconButton>
                            )}
                          </Stack>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>

            {/* Action Buttons */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              mt: 4,
              flexWrap: 'wrap'
            }}>
              <Button
                variant="outlined"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={isDisabled}
                sx={{
                  borderColor: primaryColor,
                  color: primaryColor,
                  '&:hover': {
                    backgroundColor: `${primaryColor}10`,
                    borderColor: primaryColor,
                  },
                  minWidth: 120,
                }}
              >
                Save
              </Button>
              <Button
                variant="contained"
                endIcon={<NextIcon />}
                onClick={handleNext}
                disabled={isDisabled}
                sx={{
                  backgroundColor: primaryColor,
                  '&:hover': {
                    backgroundColor: primaryColor,
                    opacity: 0.9,
                  },
                  minWidth: 120,
                }}
              >
                Next
              </Button>
            </Box>
          </Box>
        </Paper>
      </form>
    </Box>
  );
};

export default EditCompany;